import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useParams, Link, useSearchParams } from 'react-router-dom'
import newsService from '../services/newsService'
import FilterBar from '../components/news/FilterBar'
import NewsGrid from '../components/news/NewsGrid'
import TelegramBanner from '../components/news/TelegramBanner'
import SEO from '../components/common/SEO'
import {
  HiOutlineArrowLeft,
  HiOutlineBookmark,
  HiOutlineShare,
  HiOutlineLightBulb,
  HiOutlineNewspaper,
  HiOutlineRefresh,
  HiOutlineChevronDown,
} from 'react-icons/hi'
import { format } from 'date-fns'
import { vi } from 'date-fns/locale'
import clsx from 'clsx'
import toast from 'react-hot-toast'

function NewsHub() {
  const { id } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const loadMoreRef = useRef(null)
  const skeletonRef = useRef(null)
  const newContentRef = useRef(null)
  const [newItemsStartIndex, setNewItemsStartIndex] = useState(null)

  // News state
  const [news, setNews] = useState([])
  const [savedNewsIds, setSavedNewsIds] = useState([]) // Track saved news IDs
  const [selectedNews, setSelectedNews] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 20,
    total_pages: 0,
    has_more: false,
    cursor: null, // { after_id, after_time } cho cursor-based pagination
  })

  // Filter state - sync with URL params
  // Mặc định trang chủ: is_hot=true, sort=latest (không hiển thị trên URL)
  const [filters, setFilters] = useState({
    category: searchParams.get('category') || '',
    customer_type: searchParams.get('customer_type') || 'all',
    location: searchParams.get('location') || '',
    time_range: searchParams.get('time_range') || '',
    sentiment: searchParams.get('sentiment') || '',
    price_segment: searchParams.get('price_segment') || '',
    sort: searchParams.get('sort') || 'latest',
    search: searchParams.get('q') || '',
    // Mặc định is_hot=true nếu không có param nào khác (trang chủ)
    is_hot: searchParams.get('is_hot') !== null
      ? searchParams.get('is_hot') === 'true'
      : !searchParams.get('category'), // Mặc định true nếu không có category
  })

  // Load news list (initial or refresh)
  const loadNews = useCallback(async (page = 1, append = false) => {
    if (append) {
      setLoadingMore(true)
    } else {
      setLoading(true)
    }

    try {
      const params = {
        page,
        limit: 20,
        sort: filters.sort,
      }

      // Add filters
      if (filters.category) params.category = filters.category
      if (filters.customer_type && filters.customer_type !== 'all') {
        params.customer_type = filters.customer_type
      }
      if (filters.location) params.location = filters.location
      if (filters.time_range) params.time_range = filters.time_range
      if (filters.sentiment) params.sentiment = filters.sentiment
      if (filters.price_segment) params.price_segment = filters.price_segment
      if (filters.is_hot) params.is_hot = true
      if (filters.search) params.search = filters.search

      const response = await newsService.getNews(params)

      if (response.status === 'success') {
        if (append) {
          // Append new items with animation delay
          setNews(prev => [...prev, ...(response.data || [])])
        } else {
          setNews(response.data || [])
        }
        if (response.pagination) {
          setPagination(response.pagination)
        }
      }
    } catch (error) {
      console.error('Failed to load news:', error)
      toast.error('Không thể tải tin tức')
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }, [filters])

  // Load news detail
  const loadNewsDetail = async (newsId) => {
    setLoading(true)
    try {
      const response = await newsService.getNewsDetail(newsId)
      if (response.status === 'success') {
        setSelectedNews(response.data)
      }
    } catch (error) {
      console.error('Failed to load news detail:', error)
      toast.error('Không thể tải chi tiết tin')
    } finally {
      setLoading(false)
    }
  }

  // Load saved news IDs on mount
  const loadSavedNewsIds = async () => {
    try {
      const response = await newsService.getSavedNews()
      if (response.status === 'success' && response.data) {
        const ids = response.data.map(item => item.id || item.news_id)
        setSavedNewsIds(ids)
      }
    } catch (error) {
      console.error('Failed to load saved news IDs:', error)
    }
  }

  // Effect: Load news or detail based on route
  useEffect(() => {
    if (id) {
      loadNewsDetail(id)
    } else {
      loadNews()
    }
  }, [id, loadNews])

  // Load saved news IDs on initial mount
  useEffect(() => {
    loadSavedNewsIds()
  }, [])

  // Update URL when filters change
  // Không hiển thị params mặc định (is_hot=true, sort=latest) trên URL
  useEffect(() => {
    if (!id) {
      const params = new URLSearchParams()
      if (filters.category) params.set('category', filters.category)
      if (filters.customer_type && filters.customer_type !== 'all') {
        params.set('customer_type', filters.customer_type)
      }
      if (filters.location) params.set('location', filters.location)
      if (filters.time_range) params.set('time_range', filters.time_range)
      if (filters.sentiment) params.set('sentiment', filters.sentiment)
      if (filters.price_segment) params.set('price_segment', filters.price_segment)
      if (filters.sort && filters.sort !== 'latest') params.set('sort', filters.sort)
      if (filters.search) params.set('q', filters.search)
      // Chỉ hiển thị is_hot=true trên URL nếu có category (không phải mặc định)
      // Hoặc is_hot=false khi user tắt
      if (filters.is_hot && filters.category) {
        params.set('is_hot', 'true')
      } else if (!filters.is_hot && !filters.category) {
        // User đã tắt is_hot từ mặc định
        params.set('is_hot', 'false')
      }

      setSearchParams(params, { replace: true })
    }
  }, [filters, id, setSearchParams])

  // Handle save news (toggle save/unsave)
  const handleSave = async (newsId) => {
    const isSaved = savedNewsIds.includes(newsId)
    try {
      if (isSaved) {
        // Unsave
        await newsService.unsaveNews(newsId)
        setSavedNewsIds(prev => prev.filter(id => id !== newsId))
        toast.success('Đã bỏ lưu tin!')
      } else {
        // Save
        await newsService.saveNews(newsId)
        setSavedNewsIds(prev => [...prev, newsId])
        toast.success('Đã lưu tin!')
      }
    } catch (error) {
      console.error('Failed to save/unsave news:', error)
      toast.error(isSaved ? 'Không thể bỏ lưu tin' : 'Không thể lưu tin')
    }
  }

  // Handle share news
  const handleShare = async (newsId) => {
    try {
      await newsService.shareNews(newsId, { customer_type: 'all' })
      toast.success('Đã đánh dấu chia sẻ!')
    } catch (error) {
      console.error('Failed to share news:', error)
      toast.error('Không thể chia sẻ')
    }
  }

  // Handle filter changes from FilterBar
  const handleFiltersChange = (newFilters) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
    }))
  }

  // Handle search
  const handleSearch = (query) => {
    setFilters((prev) => ({
      ...prev,
      search: query,
    }))
  }

  // Handle load more with smooth scroll - SỬ DỤNG CURSOR PAGINATION
  const handleLoadMore = async () => {
    console.log('[LoadMore] Called, has_more:', pagination.has_more, 'loadingMore:', loadingMore)
    if (pagination.has_more && !loadingMore) {
      console.log('[LoadMore] Starting load, cursor:', pagination.cursor)
      const currentCount = news.length

      // 1. Show skeleton immediately & scroll to it
      setLoadingMore(true)

      // Wait for skeleton to render, then scroll
      requestAnimationFrame(() => {
        skeletonRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        })
      })

      // 2. Fetch data với CURSOR pagination (tránh trùng lặp)
      try {
        const params = {
          limit: 20,
          sort: filters.sort,
        }

        // Sử dụng cursor từ pagination (thay vì page number)
        // Cursor giúp tránh trùng lặp khi có tin mới được crawl
        if (pagination.cursor) {
          params.after_id = pagination.cursor.after_id
          params.after_time = pagination.cursor.after_time
        } else {
          // Fallback: dùng page number nếu không có cursor
          params.page = pagination.page + 1
        }

        if (filters.category) params.category = filters.category
        if (filters.customer_type && filters.customer_type !== 'all') {
          params.customer_type = filters.customer_type
        }
        if (filters.location) params.location = filters.location
        if (filters.time_range) params.time_range = filters.time_range
        if (filters.sentiment) params.sentiment = filters.sentiment
        if (filters.price_segment) params.price_segment = filters.price_segment
        if (filters.is_hot) params.is_hot = true
        if (filters.search) params.search = filters.search

        console.log('[LoadMore] Params:', params)
        const response = await newsService.getNews(params)
        console.log('[LoadMore] Response:', response)

        if (response.status === 'success') {
          // 3. Lọc tin trùng lặp (phòng trường hợp edge case)
          const existingIds = new Set(news.map(n => n.id))
          const newItems = (response.data || []).filter(item => !existingIds.has(item.id))

          if (newItems.length > 0) {
            // 4. Mark where new items start (for animation)
            setNewItemsStartIndex(currentCount)

            // 5. Add new items (đã lọc trùng)
            setNews(prev => [...prev, ...newItems])
          }

          if (response.pagination) {
            setPagination(response.pagination)
          }

          // 6. Hide skeleton after data is set
          setLoadingMore(false)

          // 7. Scroll to new content after render
          if (newItems.length > 0) {
            requestAnimationFrame(() => {
              setTimeout(() => {
                newContentRef.current?.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start'
                })
              }, 100)
            })

            // 8. Clear the new items marker after animation completes
            setTimeout(() => {
              setNewItemsStartIndex(null)
            }, 1000)
          }
        } else {
          // Response không thành công
          console.error('[LoadMore] Response not success:', response)
          toast.error(response.message || 'Không thể tải thêm tin')
          setLoadingMore(false)
        }
      } catch (error) {
        console.error('Failed to load more news:', error)
        toast.error('Không thể tải thêm tin')
        setLoadingMore(false)
      }
    }
  }

  // News detail view
  if (id && selectedNews) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-6">
        <SEO
          title={selectedNews.title}
          description={selectedNews.summary || selectedNews.title}
          image={selectedNews.thumbnail_url}
          url={`/news/${selectedNews.id}`}
          type="article"
        />
        <Link
          to="/news"
          className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4 font-medium"
        >
          <HiOutlineArrowLeft className="w-5 h-5 mr-1" />
          Quay lại danh sách
        </Link>

        <article className="card overflow-hidden">
          {selectedNews.thumbnail_url && (
            <img
              src={selectedNews.thumbnail_url}
              alt=""
              className="w-full h-48 sm:h-64 object-cover"
            />
          )}

          <div className="p-6">
            {/* Meta */}
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
              <span className="font-medium text-primary-600">
                {selectedNews.source_name || (typeof selectedNews.source === 'object'
                  ? selectedNews.source?.name
                  : selectedNews.source)}
              </span>
              <span>•</span>
              <span>
                {selectedNews.location_name || (typeof selectedNews.location === 'object'
                  ? selectedNews.location?.name
                  : selectedNews.location)}
              </span>
              {selectedNews.is_hot && (
                <>
                  <span>•</span>
                  <span className="badge-danger">HOT</span>
                </>
              )}
            </div>

            {/* Title */}
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
              {selectedNews.title}
            </h1>

            {/* Published date */}
            {selectedNews.published_at && (
              <p className="text-sm text-gray-500 mb-4">
                Đăng: {format(new Date(selectedNews.published_at), "dd/MM/yyyy 'lúc' HH:mm", { locale: vi })}
              </p>
            )}

            {/* Summary */}
            {selectedNews.summary && (
              <div className="bg-blue-50 p-4 rounded-lg mb-6">
                <h3 className="font-medium text-blue-900 mb-2 flex items-center">
                  <HiOutlineLightBulb className="w-5 h-5 mr-2" />
                  Tóm tắt cho Sales
                </h3>
                <p className="text-blue-800">{selectedNews.summary}</p>
              </div>
            )}

            {/* Content */}
            <div
              className="prose prose-sm max-w-none mb-6"
              dangerouslySetInnerHTML={{ __html: selectedNews.content || selectedNews.description }}
            />

            {/* Usage suggestions */}
            {selectedNews.usage_suggestions?.length > 0 && (
              <div className="border-t pt-6 mt-6">
                <h3 className="font-semibold text-gray-900 mb-4">
                  Gợi ý sử dụng tin này
                </h3>
                <div className="space-y-3">
                  {selectedNews.usage_suggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      className="bg-gray-50 p-4 rounded-lg"
                    >
                      <div className="flex items-start gap-2 mb-2">
                        <span className={clsx(
                          'badge',
                          suggestion.customer_type === 'dau_tu' ? 'badge-success' :
                          suggestion.customer_type === 'mua_o' ? 'badge-primary' :
                          'badge-warning'
                        )}>
                          {suggestion.customer_type === 'dau_tu' ? 'Khách đầu tư' :
                           suggestion.customer_type === 'mua_o' ? 'Khách mua ở' :
                           'Tất cả khách'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 mb-2">{suggestion.reason}</p>
                      <ul className="text-sm text-gray-600 list-disc list-inside">
                        {suggestion.talking_points?.map((point, i) => (
                          <li key={i}>{point}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Related news */}
            {selectedNews.related_news?.length > 0 && (
              <div className="border-t pt-6 mt-6">
                <h3 className="font-semibold text-gray-900 mb-4">
                  Tin liên quan
                </h3>
                <div className="grid gap-3">
                  {selectedNews.related_news.map((related) => (
                    <Link
                      key={related.id}
                      to={`/news/${related.id}`}
                      className="flex gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      {related.image_url && (
                        <img
                          src={related.image_url}
                          alt=""
                          className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900 line-clamp-2 text-sm">
                          {related.title}
                        </h4>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 mt-6 pt-6 border-t">
              <button
                onClick={() => handleSave(selectedNews.id)}
                className="btn-outline flex-1"
              >
                <HiOutlineBookmark className="w-5 h-5 mr-2" />
                Lưu tin
              </button>
              <button
                onClick={() => handleShare(selectedNews.id)}
                className="btn-primary flex-1"
              >
                <HiOutlineShare className="w-5 h-5 mr-2" />
                Gửi cho khách
              </button>
            </div>
          </div>
        </article>
      </div>
    )
  }

  // News list view
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <SEO
        title="Tin tức BĐS miền Trung"
        description="Tin tức bất động sản Đà Nẵng, Huế, Quy Nhơn và các tỉnh miền Trung - Cập nhật mới nhất từ Đất Xanh Miền Trung"
        url="/news"
      />

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <HiOutlineNewspaper className="w-8 h-8 text-primary-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Trung tâm tin tức</h1>
              <p className="text-sm text-gray-500">
                Tin tức BĐS được tổng hợp và phân tích cho Sales
              </p>
            </div>
          </div>

          <button
            onClick={() => loadNews(1)}
            disabled={loading}
            className="btn-outline flex items-center gap-2"
          >
            <HiOutlineRefresh className={clsx("w-5 h-5", loading && "animate-spin")} />
            <span className="hidden sm:inline">Làm mới</span>
          </button>
        </div>
      </div>

      {/* Telegram Banner */}
      <TelegramBanner />

      {/* Filter Bar */}
      <FilterBar
        activeCategory={filters.is_hot ? 'hot' : filters.category || 'all'}
        onCategoryChange={(cat) =>
          setFilters((prev) => ({ ...prev, category: cat === 'all' ? '' : cat, is_hot: false }))
        }
        activeCustomerType={filters.customer_type}
        onCustomerTypeChange={(ct) =>
          setFilters((prev) => ({ ...prev, customer_type: ct }))
        }
        searchQuery={filters.search}
        onSearch={handleSearch}
        sortBy={filters.sort}
        onSortChange={(sort) =>
          setFilters((prev) => ({ ...prev, sort }))
        }
        location={filters.location}
        onLocationChange={(loc) =>
          setFilters((prev) => ({ ...prev, location: loc }))
        }
        timeRange={filters.time_range}
        onTimeRangeChange={(tr) =>
          setFilters((prev) => ({ ...prev, time_range: tr }))
        }
        sentiment={filters.sentiment}
        onSentimentChange={(s) =>
          setFilters((prev) => ({ ...prev, sentiment: s }))
        }
        priceSegment={filters.price_segment}
        onPriceSegmentChange={(ps) =>
          setFilters((prev) => ({ ...prev, price_segment: ps }))
        }
        showAdvanced={true}
        onFiltersChange={handleFiltersChange}
      />

      {/* News Grid */}
      <NewsGrid
        news={news}
        loading={loading}
        onSave={handleSave}
        onShare={handleShare}
        savedNewsIds={savedNewsIds}
        newItemsStartIndex={newItemsStartIndex}
        newContentRef={newContentRef}
      />

      {/* Load More Section */}
      {pagination.has_more && !loading && (
        <div ref={loadMoreRef} className="mt-8">
          {/* Loading skeleton when loading more - matches exact news grid layout */}
          {loadingMore && (
            <div ref={skeletonRef} className="space-y-6 mb-8">
              {/* Grid skeleton - matches GridCard layout */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div
                    key={i}
                    className="card overflow-hidden relative"
                    style={{ animationDelay: `${i * 100}ms` }}
                  >
                    {/* Shimmer overlay */}
                    <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/40 to-transparent z-10" />

                    {/* Image skeleton with gradient */}
                    <div className="aspect-video bg-gradient-to-br from-secondary-200 via-secondary-100 to-secondary-200 relative overflow-hidden">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-14 h-14 rounded-xl bg-secondary-300/50 flex items-center justify-center">
                          <HiOutlineNewspaper className="w-8 h-8 text-secondary-400/50" />
                        </div>
                      </div>
                    </div>

                    {/* Content skeleton */}
                    <div className="p-3 space-y-2">
                      <div className="flex items-center gap-1.5">
                        <div className="h-3 w-16 bg-secondary-200 rounded animate-pulse" />
                        <div className="h-3 w-2 bg-secondary-200 rounded animate-pulse" />
                        <div className="h-3 w-12 bg-secondary-200 rounded animate-pulse" />
                      </div>
                      <div className="space-y-1.5">
                        <div className="h-4 w-full bg-secondary-200 rounded animate-pulse" />
                        <div className="h-4 w-4/5 bg-secondary-200 rounded animate-pulse" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Compact list skeleton - matches CompactCard layout */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="card overflow-hidden relative"
                    style={{ animationDelay: `${(i + 6) * 100}ms` }}
                  >
                    {/* Shimmer overlay */}
                    <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/40 to-transparent z-10" />

                    <div className="flex">
                      {/* Thumbnail skeleton */}
                      <div className="w-32 sm:w-40 flex-shrink-0 bg-gradient-to-br from-secondary-200 via-secondary-100 to-secondary-200 min-h-[120px] relative">
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-10 h-10 rounded-lg bg-secondary-300/50 flex items-center justify-center">
                            <HiOutlineNewspaper className="w-6 h-6 text-secondary-400/50" />
                          </div>
                        </div>
                      </div>

                      {/* Content skeleton */}
                      <div className="flex-1 p-3 space-y-2">
                        <div className="flex items-center gap-2">
                          <div className="h-3 w-20 bg-secondary-200 rounded animate-pulse" />
                          <div className="h-3 w-2 bg-secondary-200 rounded animate-pulse" />
                          <div className="h-3 w-16 bg-secondary-200 rounded animate-pulse" />
                        </div>
                        <div className="space-y-1.5">
                          <div className="h-4 w-full bg-secondary-200 rounded animate-pulse" />
                          <div className="h-4 w-3/4 bg-secondary-200 rounded animate-pulse" />
                        </div>
                        <div className="flex items-center justify-between pt-1">
                          <div className="h-5 w-16 bg-secondary-200 rounded animate-pulse" />
                          <div className="h-6 w-6 bg-secondary-200 rounded animate-pulse" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Load more button */}
          {!loadingMore && (
            <div className="text-center">
              <button
                onClick={handleLoadMore}
                className="inline-flex items-center gap-2 px-8 py-3 rounded-xl font-medium transition-all bg-white border border-secondary-200 text-secondary-700 hover:bg-secondary-50 hover:border-secondary-300 hover:shadow-md"
              >
                <HiOutlineChevronDown className="w-5 h-5" />
                <span>Xem thêm {pagination.total - news.length} tin</span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Stats */}
      {!loading && news.length > 0 && (
        <div className="mt-6 text-center text-sm text-gray-500">
          Hiển thị {news.length} / {pagination.total} tin tức
        </div>
      )}

      {/* Custom CSS for animations */}
      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.4s ease-out forwards;
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shimmer {
          animation: shimmer 1.5s infinite;
        }
      `}</style>
    </div>
  )
}

export default NewsHub
